import type { FocusEvent, FunctionComponent, KeyboardEvent } from "react";
import { useCallback, useRef } from "react";

import { notEmpty } from "shared/lib/utils";

import { getResponsiveProps } from "scmp-app/components/header/features/helpers";
import { HeaderFeaturesSearchBarTooltip } from "scmp-app/components/header/features/search-bar-tooltip";
import type { HeaderFeatureCommonProps } from "scmp-app/components/header/features/types";
import { useMastheadTracking } from "scmp-app/components/header/hooks";
import { MaxKeywordLength, SearchDefaultPlaceholder } from "scmp-app/components/search/consts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import { useDefaultSearchInputState } from "scmp-app/components/search/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { useIntersection } from "scmp-app/lib/hooks";
import { useRouteHandler } from "scmp-app/lib/router/hooks";
import { page as SearchPage } from "scmp-app/pages/search";

import {
  Container,
  SearchBarContainer,
  SearchBarInput,
  SearchIcon,
  SearchIconButton,
  TooltipTarget,
} from "./styles";

export type HeaderFeaturesSearchBarProps = HeaderFeatureCommonProps;

export const HeaderFeaturesSearchBar: FunctionComponent<HeaderFeaturesSearchBarProps> = props => {
  const routePush = useRouteHandler();
  const mastheadQueryParameter = useMastheadTracking();
  const [value, setValue] = useDefaultSearchInputState();
  const inputReference = useRef<HTMLInputElement | null>(null);

  const { captureIntersectionElement, intersection, setShouldDisable } = useIntersection({});

  const handleSearch = useCallback(() => {
    if (!notEmpty(value)) {
      inputReference.current?.focus();
      return;
    }

    // GA4 21.1.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      subcategory: "icon",
    } as SearchEvent);

    const sanitizedValue = sanitizeKeyword(value);
    const { asPath, query } = SearchPage.route(
      notEmpty(sanitizedValue) ? { q: sanitizedValue } : {},
    );
    void routePush(asPath, { query: { ...query, ...mastheadQueryParameter } });
  }, [mastheadQueryParameter, routePush, value]);

  const handleInputKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") return;
      handleSearch();
    },
    [handleSearch],
  );

  const handleOnFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    requestAnimationFrame(() => {
      event.target.setSelectionRange(0, 9999);
      event.target.select();
    });
  }, []);

  return (
    <Container ref={captureIntersectionElement} {...getResponsiveProps(props)}>
      <HeaderFeaturesSearchBarTooltip
        onClose={() => setShouldDisable(true)}
        visible={intersection?.isIntersecting === true}
      >
        <TooltipTarget />
      </HeaderFeaturesSearchBarTooltip>
      <SearchBarContainer>
        <SearchBarInput
          aria-label="search input"
          maxLength={MaxKeywordLength}
          onChange={event => setValue(event.target.value)}
          onFocus={handleOnFocus}
          onKeyUp={handleInputKeyUp}
          placeholder={SearchDefaultPlaceholder}
          ref={inputReference}
          type="text"
          value={value}
        />
        <SearchIconButton aria-label="search" onClick={handleSearch} type="button">
          <SearchIcon />
        </SearchIconButton>
      </SearchBarContainer>
    </Container>
  );
};

HeaderFeaturesSearchBar.displayName = "HeaderFeaturesSearchBar";
